import { Injectable, inject } from '@angular/core';
import { StorageStateService } from './storage-state.service';
import * as localforage from 'localforage';
import { CONFIRM_SERVICE } from '../../core/tokens';

@Injectable({
	providedIn: 'root'
})
export class StorageService extends StorageStateService {
	private confirmService = inject(CONFIRM_SERVICE, { optional: true });
	private global!: LocalForage;
	private stores = new Map<string, LocalForage>();

	constructor() {
		super();
		this.init();
	}

	private init() {
		this.global = localforage.createInstance({ name: 'global' });
	}

	private _getStore(name: string, store = 'keyvalue'): LocalForage | undefined {
		if (!this.stores.has(`${name}##${store}`)) {
			console.log(`Creating localforage instance for ${name} / ${store}`);
			this.stores.set(`${name}##${store}`, localforage.createInstance({ name: name, storeName: store }));
		}
		return this.stores.get(`${name}##${store}`);
	}

	async get(key: string): Promise<any> {
		const val = await this.global.getItem(key);
		const stringified = JSON.stringify(val);
		localStorage.setItem(key, stringified);
		if (val === null) return undefined;
		return val;
	}

	getSync(key: string): any {
		try {
			return JSON.parse(localStorage.getItem(key) || 'null');
		} catch (e) {
			return undefined;
		}
	}

	async set(key: string, value: any) {
		const stringified = JSON.stringify(value);
		localStorage.setItem(key, stringified);
		await this.global.setItem(key, value);
	}

	setSync(key: string, value: any) {
		const stringified = JSON.stringify(value);
		localStorage.setItem(key, stringified);
		this.global.setItem(key, value);
	}

	async remove(key: string) {
		localStorage.removeItem(key);
		await this.global.removeItem(key);
	}

	removeSync(key: string) {
		localStorage.removeItem(key);
		this.global.removeItem(key);
	}

	async getProjectValue(key: string, project: string, store = 'keyvalue') {
		const st = this._getStore(project, store);
		if (!st) return Promise.resolve(undefined);
		try {
			return await st.getItem<any>(key);
		} catch (error: any) {
			if (error?.name === 'InvalidStateError' || error?.name === 'NotFoundError') {
				console.warn(`IndexedDB connection error for ${project}/${store}, recreating store`);
				const storeKey = `${project}##${store}`;
				this.stores.delete(storeKey);
				const newSt = this._getStore(project, store);
				if (!newSt) return undefined;
				return await newSt.getItem<any>(key);
			}
			throw error;
		}
	}

	async setProjectValue(key: string, project: string, value: any, store = 'keyvalue') {
		const st = this._getStore(project, store);
		if (!st) return;
		try {
			return await st.setItem(key, value);
		} catch (error: any) {
			if (error?.name === 'InvalidStateError' || error?.name === 'NotFoundError') {
				console.warn(`IndexedDB connection error for ${project}/${store}, recreating store`);
				const storeKey = `${project}##${store}`;
				this.stores.delete(storeKey);
				const newSt = this._getStore(project, store);
				if (!newSt) return;
				return await newSt.setItem(key, value);
			}
			throw error;
		}
	}

	async removeProjectValue(key: string, project: string, store = 'keyvalue') {
		const st = this._getStore(project, store);
		if (!st) return;
		try {
			await st.removeItem(key);
		} catch (error: any) {
			if (error?.name === 'InvalidStateError' || error?.name === 'NotFoundError') {
				console.warn(`IndexedDB connection error for ${project}/${store}, recreating store`);
				const storeKey = `${project}##${store}`;
				this.stores.delete(storeKey);
				const newSt = this._getStore(project, store);
				if (!newSt) return;
				await newSt.removeItem(key);
			} else {
				throw error;
			}
		}
	}

	async getProject(project: string) {
		const st = await this._getStore(project, 'keyvalue');
		if (!st) return Promise.resolve(undefined);
		const keys = await st.keys();
		const pairs = await Promise.all(
			keys.map(async (key) => ({
				key,
				value: await st.getItem(key)
			})));
		return pairs;
	}

	async setProject(prData: any) {
		const ignore = new Set<string>(['_id', '_rev']);
		await Promise.all(
			Object.keys(prData)
				.filter((key: string) => !ignore.has(key))
				.map(async (key: string) =>
					await this.setProjectValue(key, prData._id, prData[key])
				)
		);
	}

	async numberOfKeys(project: string, store = 'keyvalue'): Promise<number | undefined> {
		const st = this._getStore(project, store);
		if (!st) return Promise.resolve(undefined);
		try {
			return await st.length();
		} catch (error: any) {
			if (error?.name === 'InvalidStateError' || error?.name === 'NotFoundError') {
				console.warn(`IndexedDB connection error for ${project}/${store}, recreating store`);
				const storeKey = `${project}##${store}`;
				this.stores.delete(storeKey);
				const newSt = this._getStore(project, store);
				if (!newSt) return undefined;
				return await newSt.length();
			}
			throw error;
		}
	}

	async clearProject(project: string, mapsOnly?: boolean) {
		const projectStores = [...this.stores.entries()].filter(([key]) => key.startsWith(`${project}##`) && (!mapsOnly || !key.endsWith('keyvalue')));
		await Promise.all(projectStores.map(([, store]) => store.clear()));
		projectStores.forEach(([key]) => this.stores.delete(key));
		await localforage.dropInstance({ name: project });
	}

	async listKeys(project: string, store = 'keyvalue'): Promise<string[] | undefined> {
		const st = this._getStore(project, store);
		if (!st) return undefined;
		try {
			return await st.keys();
		} catch (error: any) {
			if (error?.name === 'InvalidStateError' || error?.name === 'NotFoundError') {
				console.warn(`IndexedDB connection error for ${project}/${store}, recreating store`);
				const storeKey = `${project}##${store}`;
				this.stores.delete(storeKey);
				const newSt = this._getStore(project, store);
				if (!newSt) return undefined;
				return await newSt.keys();
			}
			throw error;
		}
	}

	async cleanUp() {
		if (this.confirmService) {
			const confirmed = await this.confirmService.confirmDelete(
				'Soll der lokale Speicher bereinigt werden?\n\nDies löscht alle lokal gespeicherten Daten.',
				'Speicher bereinigen'
			);
			if (!confirmed) return;
		}
		await this.cleanUpSilent();
		window.location.reload();
	}

	async cleanUpSilent(): Promise<void> {
		console.log('[Storage] Bereinige lokalen Speicher...');

		localStorage.clear();
		localforage.clear();
		await Promise.all(Array.from(this.stores.values()).map(async (store) => await store.clear()));
		this.stores.clear();
		await this.global.clear();

		const dbs = await indexedDB?.databases();
		dbs?.forEach((db) => {
			if (db.name) {
				indexedDB.deleteDatabase(db.name!);
			}
		});

		if ('caches' in window) {
			const keys = await caches.keys();
			await Promise.all(keys.map(async key => await caches.delete(key)));
		}

		if ('serviceWorker' in navigator) {
			const registrations = await navigator.serviceWorker.getRegistrations();
			await Promise.all(registrations.map(async reg => await reg.unregister()));
		}

		console.log('[Storage] Bereinigung abgeschlossen');
	}
}
